import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tag } from "@msuite/ui/tag";
import { guardString } from "@msuite/utils/strings";
import {
	formatInvoiceAmount,
	IncomingInvoiceKindTag,
	IncomingInvoiceStateTag,
} from "@/components/incoming-invoices";
import { useIncomingInvoice } from "@/hooks/use-incoming-invoice";
import { IncomingInvoiceDuplicateTag } from "./incoming-invoice-duplicate-tag";
import { IncomingInvoiceRemindedTag } from "./incoming-invoice-reminded-tag";
import type { IncomingInvoicePanelBaseProps } from "./shared";

export function IncomingInvoiceHeader({ incomingInvoiceId }: IncomingInvoicePanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { incomingInvoice } = useIncomingInvoice(incomingInvoiceId);

	/** Computed */
	const title = incomingInvoice?.number
		? t("incoming_invoices.invoice_n", { number: incomingInvoice.number })
		: t("incoming_invoices.incoming_invoice");
	// The booked contact when there is one, otherwise the company as the invoice prints it — a
	// partner's invoice carries no contact.
	const issuer = incomingInvoice?.contact?.name ?? incomingInvoice?.issuerName;
	// Gross is what is paid; net stands in only when the gross could not be read.
	const isGross = incomingInvoice?.grossAmount !== null;
	const amount = isGross ? incomingInvoice?.grossAmount : incomingInvoice?.netAmount;

	/** Render */
	return (
		<ContentCard
			title={title}
			icon={icon("ReceiptText")}
			// The number is what tells one invoice from the next in the breadcrumb, not the uuid.
			identifier={incomingInvoice ? incomingInvoiceId : undefined}
			dataPageBreadcrumbsTitle={incomingInvoice ? title : undefined}
			accessory={
				incomingInvoice ? (
					<div className="h-stack">
						{incomingInvoice.extractionState === "failed" && (
							<Tag
								size="xs"
								color="rose"
								variant="plain"
							>
								{t("incoming_invoices.extraction_failed")}
							</Tag>
						)}
						{incomingInvoice.state === "duplicate" && incomingInvoice.duplicateOf && (
							<IncomingInvoiceDuplicateTag original={incomingInvoice.duplicateOf} />
						)}
						{incomingInvoice.eInvoiceFormat && (
							<Tag
								size="xs"
								color="emerald"
								variant="plain"
							>
								{t(`incoming_invoices.e_invoice_format.${incomingInvoice.eInvoiceFormat}`)}
							</Tag>
						)}
						<IncomingInvoiceRemindedTag reminders={incomingInvoice.reminders} />
						<IncomingInvoiceStateTag state={incomingInvoice.state} />
					</div>
				) : undefined
			}
		>
			<div className="v-stack gap-3">
				<span className="font-medium">{guardString(issuer)}</span>
				{incomingInvoice && (
					<div className="h-stack w-full justify-between">
						<IncomingInvoiceKindTag kind={incomingInvoice.kind} />
						<div className="h-stack items-baseline gap-1.5">
							<span className="font-semibold tabular-nums">
								{formatInvoiceAmount(amount ?? null, incomingInvoice.currency)}
							</span>
							<span className="label">
								{t(isGross ? "incoming_invoices.gross_hint" : "incoming_invoices.net_hint")}
							</span>
						</div>
					</div>
				)}
			</div>
		</ContentCard>
	);
}
